import {Settings} from '@mui/icons-material';
import {Box, Button, Divider, Grid} from '@mui/material';
import {FC, useMemo} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {authApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AppDispatch} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {btnTextSx, configButtonSx, configWrapperSx, resetButtonsWrapperSx,} from './UserResetWrapper.sx';
import {actionButtonSx} from 'src/components/common/actionButton.sx';
import {LockResetOutlined} from '@mui/icons-material';
import {UserResetWrapperProps} from './UserResetWrapper.types';

type ResetUserPasswordMutation = ReturnType<typeof authApi.useLazyForgotPasswordQuery>[0];

const handleResetUserPassword =
    (
        trigger: ResetUserPasswordMutation,
        dispatch: AppDispatch,
        emailAddress: string,
        successMessage: string
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({emailAddress});
                NotifySuccess(successMessage);
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const UserResetWrapper: FC<UserResetWrapperProps> = ({data, toolbar}) => {
    const [trigger] = authApi.useLazyForgotPasswordQuery();

    const dispatch = useAppDispatch();
    const {activeBusiness} = useAppSelector((state) => state.common);

    const intl = useIntl();

    const successMessage = intl.formatMessage({id: 'emailWithInstructions'});

    const email = useMemo<string>(() => {
        if (!data) {
            return '';
        }

        if ('emailAddress' in data) {
            return data.emailAddress;
        }

        if ('user' in data && 'emailAddress' in data.user) {
            return data.user.emailAddress;
        }

        return '';
    }, [data]);

    const onReset = handleResetUserPassword(trigger, dispatch, email, successMessage);

    if (toolbar) {
        return (
            <Button
                variant="contained"
                sx={actionButtonSx('32px', 'secondary')}
                startIcon={<LockResetOutlined/>}
                onClick={onReset}
            >
                <FormattedMessage id="resetPassword"/>
            </Button>
        );
    }

    return (
        <>
            <Grid
                item
                xs={12}
            >
                <Divider/>
            </Grid>
            <Grid
                item
                xs={12}
            >
                <Box sx={configWrapperSx}>
                    <Box sx={resetButtonsWrapperSx}>
                        <Button
                            sx={configButtonSx}
                            onClick={handleResetUserPassword(trigger, dispatch, email, successMessage)}
                        >
                            <Settings/>
                            <Box
                                component="span"
                                sx={btnTextSx}
                            >
                                <FormattedMessage id="resetPassword"/>
                            </Box>
                        </Button>
                    </Box>
                </Box>
            </Grid>
        </>
    );
};
